import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { type FormEvent, useEffect, useState } from "react";
import {
  createDocumentTemplate,
  DOCUMENT_TEMPLATE_TYPES,
  type DocumentTemplate,
  type DocumentTemplateType,
  deleteDocumentTemplate,
  listDocumentTemplates,
  updateDocumentTemplate,
} from "@/lib/api";

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

export function DocumentTemplatesTab() {
  const [templates, setTemplates] = useState<DocumentTemplate[] | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [readError, setReadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<DocumentTemplate | null>(
    null,
  );
  const [name, setName] = useState("");
  const [templateType, setTemplateType] =
    useState<DocumentTemplateType>("invoice");
  const [isDefault, setIsDefault] = useState(false);

  const load = () => {
    setLoading(true);
    setReadError(null);
    listDocumentTemplates()
      .then(setTemplates)
      .catch((cause: unknown) => setReadError(toErrorMessage(cause)))
      .finally(() => setLoading(false));
  };

  const reload = async () => {
    try {
      setTemplates(await listDocumentTemplates());
    } catch (cause) {
      setError(toErrorMessage(cause));
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setReadError(null);
    listDocumentTemplates()
      .then((rows) => {
        if (!cancelled) setTemplates(rows);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setReadError(toErrorMessage(cause));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!name.trim()) {
      setError("Give the template a name.");
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await createDocumentTemplate({
        name: name.trim(),
        type: templateType,
        is_default: isDefault,
      });
      setNotice("Template created.");
      setEditorOpen(false);
      setName("");
      setIsDefault(false);
      await reload();
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      await deleteDocumentTemplate(id);
      setNotice("Template deleted.");
      await reload();
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  const toggleDefault = async (template: DocumentTemplate) => {
    setBusy(true);
    setError(null);
    try {
      await updateDocumentTemplate(template.id, {
        is_default: !template.is_default,
      });
      await reload();
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-4">
      <p className="text-sm text-[var(--muted-foreground)]">
        Branding for printed documents — which layout is the default for each
        document type. Add a template and set it as the default; until then,
        documents use the built-in layout.
      </p>
      <div className="actions-row">
        <Button
          type="button"
          disabled={busy}
          onClick={() => setEditorOpen(true)}
        >
          New template
        </Button>
      </div>
      {error && !editorOpen ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
        </div>
      ) : null}
      {notice ? (
        <output className="surface block rounded-xl border border-[var(--success)] p-4 text-sm text-[var(--success)]">
          {notice}
        </output>
      ) : null}
      {readError ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          <p>Couldn&apos;t load document templates: {readError}</p>
          <div className="mt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={load}
              disabled={loading}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : loading ? (
        <p className="text-sm text-[var(--muted-foreground)]">
          Loading document templates…
        </p>
      ) : (
        <div className="surface overflow-hidden rounded-2xl">
          <div className="table-scroll">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Default</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(templates ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      No templates yet — documents use the built-in layout until
                      you add one.
                    </TableCell>
                  </TableRow>
                ) : (
                  (templates ?? []).map((template) => (
                    <TableRow key={template.id}>
                      <TableCell>{template.name}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {template.type}
                      </TableCell>
                      <TableCell className="text-xs">
                        {template.is_default ? "Yes" : "No"}
                      </TableCell>
                      <TableCell>
                        <span className="flex gap-2">
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            disabled={busy}
                            onClick={() => void toggleDefault(template)}
                          >
                            {template.is_default
                              ? "Unset default"
                              : "Set default"}
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={busy}
                            onClick={() => setPendingDelete(template)}
                            aria-label={`Delete ${template.name}`}
                          >
                            Delete
                          </Button>
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
      <Drawer
        open={editorOpen}
        onOpenChange={(next) => {
          if (!busy) setEditorOpen(next);
        }}
        title="New template"
        description="Name a document template and choose whether it is the default for its document type."
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={() => setEditorOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" form="document-template-form" disabled={busy}>
              {busy ? "Creating…" : "Add template"}
            </Button>
          </>
        }
      >
        {error ? (
          <p role="alert" className="mb-3 text-sm text-[var(--danger)]">
            {error}
          </p>
        ) : null}
        <form
          id="document-template-form"
          onSubmit={submit}
          className="grid gap-3"
        >
          <label
            htmlFor="template-name"
            className="grid gap-1 text-xs font-medium"
          >
            Template name
            <Input
              id="template-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="e.g. Standard invoice"
              disabled={busy}
            />
          </label>
          <label className="grid gap-1 text-xs font-medium">
            Document type
            <select
              className="select"
              value={templateType}
              onChange={(e) =>
                setTemplateType(e.target.value as DocumentTemplateType)
              }
              disabled={busy}
              aria-label="Document type"
            >
              {DOCUMENT_TEMPLATE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </label>
          <label
            htmlFor="template-default"
            className="flex items-center gap-2 text-sm font-normal"
          >
            <input
              id="template-default"
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              disabled={busy}
            />
            Default
          </label>
        </form>
      </Drawer>

      <Dialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        title={
          pendingDelete
            ? `Delete ${pendingDelete.name}?`
            : "Delete this template?"
        }
        description="Documents of this type fall back to the built-in layout. Deleting a template cannot be undone."
      >
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setPendingDelete(null)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              const template = pendingDelete;
              setPendingDelete(null);
              if (template) void remove(template.id);
            }}
          >
            Delete template
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
